import React from 'react';
import './UserNotFoundMessage.css';

interface UserNotFoundMessageProps {
  matchedUser: {
    name: string;
    fullName: string;
    avatar?: string;
    birthCity?: string;
    email?: string;
  };
  onClose: () => void;
  onSendInvitation: () => void;
  isSendingInvitation: boolean;
}

const UserNotFoundMessage: React.FC<UserNotFoundMessageProps> = ({
  matchedUser,
  onClose,
  onSendInvitation,
  isSendingInvitation
}) => {
  const hasEmail = matchedUser.email && matchedUser.email.trim() !== '';
  
  return (
    <div className="user-not-found-overlay">
      <div className="user-not-found-container">
        <div className="user-not-found-header">
          <div className="user-not-found-avatar">
            <img
              src={matchedUser.avatar || "https://encrypted-tbn0.gstatic.com/images?q=tbn:ANd9GcRPoAJ0jJpb24AFIkHZ4RfZTXFAHLLjxm33Fg&s"}
              alt={matchedUser.fullName}
              className="avatar-img"
            />
          </div>
          <div className="user-not-found-info">
            <h4 className="user-name">{matchedUser.fullName}</h4>
            {matchedUser.birthCity && (
              <p className="user-location">{matchedUser.birthCity}</p>
            )}
          </div>
          <button className="btn-close-user-not-found" onClick={onClose}>
            ×
          </button>
        </div>

        <div className="user-not-found-content">
          <div className={hasEmail ? "info-icon" : "info-icon error-icon"}>
            {hasEmail ? (
              <i className="fas fa-info-circle"></i>
            ) : (
              <i className="fas fa-exclamation-circle"></i>
            )}
          </div>
          
          <h3 className="info-title">User Not Found on iGiTREE</h3>
          
          <div className="info-message">
            <p>
              <strong>{matchedUser.fullName}</strong> is not currently registered on iGiTREE, 
              so you cannot start a chat with them directly.
            </p>
            
            {hasEmail ? (
              <p>
                However, we can help you connect! We'll send them a professional invitation 
                to join iGiTREE and connect with you.
              </p>
            ) : (
              <div className="error-message">
                <i className="fas fa-exclamation-triangle me-2"></i>
                <strong>Error: No email address available</strong>
                <p className="error-details">
                  We don't have an email address for <strong>{matchedUser.fullName}</strong>, 
                  so we are unable to send an email notification at this time. 
                  Please contact support if you have their contact information.
                </p>
              </div>
            )}
          </div>

          {hasEmail && (
            <>
              <div className="invitation-benefits">
                <h4>What happens when we invite them:</h4>
                <ul>
                  <li>
                    <i className="fas fa-envelope"></i>
                    They'll receive a professional email invitation
                  </li>
                  <li>
                    <i className="fas fa-user-plus"></i>
                    They can easily sign up for iGiTREE
                  </li>
                  <li>
                    <i className="fas fa-comments"></i>
                    You'll be able to chat once they join
                  </li>
                  <li>
                    <i className="fas fa-heart"></i>
                    They'll know you're looking to connect
                  </li>
                </ul>
              </div>

              <div className="action-buttons">
                <button
                  className="btn-send-invitation"
                  onClick={onSendInvitation}
                  disabled={isSendingInvitation}
                >
                  {isSendingInvitation ? (
                    <>
                      <span className="spinner-border spinner-border-sm me-2" role="status">
                        <span className="visually-hidden">Sending...</span>
                      </span>
                      Sending Invitation...
                    </>
                  ) : (
                    <>
                      <i className="fas fa-paper-plane me-2"></i>
                      Send Invitation
                    </>
                  )}
                </button>
                
                <button
                  className="btn-cancel"
                  onClick={onClose}
                  disabled={isSendingInvitation}
                >
                  Cancel
                </button>
              </div>

              <div className="privacy-note">
                <i className="fas fa-shield-alt"></i>
                <small>
                  We respect privacy. The invitation will be sent professionally and 
                  they can choose whether to join or not.
                </small>
              </div>
            </>
          )}

          {!hasEmail && (
            <div className="action-buttons">
              <button
                className="btn-cancel"
                onClick={onClose}
                style={{ width: '100%', maxWidth: '280px' }}
              >
                Close
              </button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default UserNotFoundMessage;
